import type { RiskLevel, PestModelType } from '../types/pest';
import { toNumOr, type Numeric } from '@/utils/numeric';

/** Display metadata per risk level — shared across the dashboard, admin and widgets. */
export const RISK_META: Record<RiskLevel, { label: string; color: string; badgeClass: string }> = {
  none: { label: 'None', color: '#94a3b8', badgeClass: 'bg-slate-100 text-slate-600' },
  low: { label: 'Low', color: '#84cc16', badgeClass: 'bg-lime-100 text-lime-700' },
  moderate: { label: 'Moderate', color: '#f59e0b', badgeClass: 'bg-amber-100 text-amber-700' },
  high: { label: 'High', color: '#f97316', badgeClass: 'bg-orange-100 text-orange-700' },
  severe: { label: 'Severe', color: '#dc2626', badgeClass: 'bg-red-100 text-red-700' },
};

export function riskColor(level: RiskLevel | string | null | undefined): string {
  return RISK_META[(level || 'none') as RiskLevel]?.color ?? RISK_META.none.color;
}

export function riskLabel(level: RiskLevel | string | null | undefined): string {
  return RISK_META[(level || 'none') as RiskLevel]?.label ?? 'None';
}

export function riskBadgeClass(level: RiskLevel | string | null | undefined): string {
  return RISK_META[(level || 'none') as RiskLevel]?.badgeClass ?? RISK_META.none.badgeClass;
}

/** Y-axis unit + accessor for the plotted accumulation, per archetype. */
export function plotMetaFor(modelType: PestModelType): { label: string; unit: string } {
  switch (modelType) {
    case 'degree_day':
      return { label: 'Degree-days', unit: '°Cd' };
    case 'infection_wetness':
    case 'generic_infection':
      return { label: 'Infection periods', unit: '' };
    default:
      return { label: 'Risk score', unit: '' };
  }
}

export function plotValue(modelType: PestModelType, d: { value_cum: Numeric; risk_score: Numeric }): number {
  if (modelType === 'risk_index') return toNumOr(d.risk_score);
  return toNumOr(d.value_cum);
}

export const KIND_LABEL: Record<string, string> = {
  disease: 'Disease',
  insect: 'Insect',
  weed: 'Weed',
};

export const MODEL_TYPE_LABEL: Record<PestModelType, string> = {
  degree_day: 'Degree-day',
  infection_wetness: 'Infection (Mills)',
  generic_infection: 'Infection (Magarey)',
  risk_index: 'Risk index',
};

/**
 * Hours of wetness the Magarey generic infection model needs at `t` °C, or
 * null where infection cannot happen. Mirrors pestModels.magareyWetnessRequired
 * on the server, which stays authoritative — this only powers the editor's
 * preview table.
 */
export function magareyWetnessRequired(
  t: number,
  p: { t_min_c: number; t_opt_c: number; t_max_c: number; w_min_h: number; w_max_h?: number },
): number | null {
  const { t_min_c: tMin, t_opt_c: tOpt, t_max_c: tMax, w_min_h: wMin, w_max_h: wMax } = p;
  if (![t, tMin, tOpt, tMax, wMin].every(Number.isFinite)) return null;
  if (!(tMin < tOpt && tOpt < tMax) || wMin <= 0 || t <= tMin || t >= tMax) return null;
  const f = ((tMax - t) / (tMax - tOpt)) * Math.pow((t - tMin) / (tOpt - tMin), (tOpt - tMin) / (tMax - tOpt));
  if (!(f > 0)) return null;
  const w = wMin / Math.min(1, f);
  return wMax != null && Number.isFinite(wMax) && w > wMax ? null : w;
}
